/**
 * Choosing a vehicle in the shared type tree (owner's idea "Választók új típus felvételénél",
 * built 2026-11-02): make → model → generation → engine variant, not too deep.
 *
 * The tree stores make, model and engine nodes; a model node is one generation ("GOLF VI (5K1)",
 * "SWIFT III (MZ, EZ)"). The picker groups the model nodes of a make by their first word (the
 * model family, "GOLF"), so the generation step shows only when a family has more than one node.
 */

export interface TreeNodeLike {
  id: string;
  name: string;
}

/** "GOLF VI (5K1)" → "GOLF"; "C-CLASS (W204)" → "C-CLASS"; a single word stays itself. */
export function modelFamily(name: string): string {
  const first = name.trim().split(/\s+|\(/)[0] ?? "";
  return (first || name.trim()).toUpperCase();
}

export interface ModelFamily<N extends TreeNodeLike> {
  family: string;
  /** Display name: the node's own name when the family has one node, else the family word. */
  label: string;
  nodes: N[];
}

/** Model nodes grouped by family, in name order; one node families keep the node's own name. */
export function groupModels<N extends TreeNodeLike>(models: readonly N[]): ModelFamily<N>[] {
  const map = new Map<string, N[]>();
  for (const m of models) {
    const f = modelFamily(m.name);
    map.set(f, [...(map.get(f) ?? []), m]);
  }
  return [...map.entries()]
    .map(([family, nodes]) => ({ family, label: nodes.length === 1 ? nodes[0]!.name : family, nodes: [...nodes].sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Case and accent insensitive filter of a list by what the user typed (every word must be found). */
export function filterNodes<N extends TreeNodeLike>(nodes: readonly N[], query: string): N[] {
  const norm = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toUpperCase();
  const parts = norm(query).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return [...nodes];
  return nodes.filter((n) => parts.every((p) => norm(n.name).includes(p)));
}

/**
 * Makers by the first three characters of a VIN (the world manufacturer identifier). Only well
 * known European market codes; an unknown code gives no make (nothing is guessed).
 */
const WMI: Record<string, string> = {
  WVW: "Volkswagen", WV1: "Volkswagen", WV2: "Volkswagen", WVG: "Volkswagen",
  WAU: "Audi", WUA: "Audi",
  WBA: "BMW", WBS: "BMW", WBY: "BMW",
  WDB: "Mercedes-Benz", WDD: "Mercedes-Benz", WDC: "Mercedes-Benz", WDF: "Mercedes-Benz", W1K: "Mercedes-Benz", W1N: "Mercedes-Benz", W1V: "Mercedes-Benz",
  W0L: "Opel", W0V: "Opel",
  WF0: "Ford",
  WP0: "Porsche",
  VF1: "Renault", VF3: "Peugeot", VF7: "Citroën", VR3: "Peugeot", UU1: "Dacia",
  VSS: "SEAT", TMB: "Škoda",
  ZFA: "Fiat", ZAR: "Alfa Romeo",
  YV1: "Volvo",
  TSM: "Suzuki", JSA: "Suzuki",
  TMA: "Hyundai", KMH: "Hyundai",
  KNA: "Kia", KNE: "Kia", U5Y: "Kia",
  JMZ: "Mazda", SJN: "Nissan", JN1: "Nissan",
  JTD: "Toyota", JTE: "Toyota", SB1: "Toyota", VNK: "Toyota",
  JHM: "Honda", SHH: "Honda",
  JMB: "Mitsubishi", JF1: "Subaru",
  SAL: "Land Rover", SAJ: "Jaguar",
};

/** The make from a VIN's manufacturer code, or null when unknown. */
export function makeFromVin(vin: string | null | undefined): string | null {
  const v = (vin ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (v.length < 3) return null;
  return WMI[v.slice(0, 3)] ?? null;
}

/** A proposed tree node with how well it fits (higher is better). */
export interface TypeCandidate {
  id: string;
  score: number;
}

/**
 * What the "from the registration" choice does with the candidates: one clear match is
 * preselected and asked ("Ez az?"), several are listed to pick, none falls back to the list.
 * Clear: the only one, or the best scores at least 2 more than the next.
 */
export function typeMatchOutcome<C extends TypeCandidate>(candidates: readonly C[]): { kind: "none" } | { kind: "one"; best: C } | { kind: "several"; list: C[] } {
  if (candidates.length === 0) return { kind: "none" };
  const sorted = [...candidates].sort((a, b) => b.score - a.score);
  if (sorted.length === 1 || sorted[0]!.score >= sorted[1]!.score + 2) return { kind: "one", best: sorted[0]! };
  return { kind: "several", list: sorted };
}
